import { LitElement, html, css } from 'lit-element';
import { ifDefined } from 'lit-html/directives/if-defined.js';

const ACTIVATION_TYPES = {
  AUTOMATIC: 'automatic',
  MANUAL: 'manual'
};

export class Tooltip extends LitElement {
  constructor() {
    super();
    this.triggerLabel = 'Show tooltip';
    this.open = false;
    /** Default is "automatic" for backwards compatibility with existing templates */
    this.activationType = ACTIVATION_TYPES.AUTOMATIC;
  }

  static get properties() {
    return {
      placement: { type: String, default: 'bottom' },
      /** Set the aria-label attribute of the trigger. */
      triggerLabel: { type: String, attribute: 'trigger-label' },
      /**
       * "automatic" - show tooltip on hover and focus
       * "manual" - show tooltip on hover and click
       */
      activationType: { type: String, attribute: 'activation-type' },
      /** Whether the tooltip is showing. Internal property; only here so that we re-render when it changes */
      open: { type: Boolean, attribute: false }
    };
  }

  static get styles() {
    return css`
      :host[hidden] {
        display: none;
      }

      :host {
        display: inline-block;
        position: relative;
        z-index: 9;
      }

      /* reset default button styles */
      button.trigger {
        all: unset;
      }

      /* do not reset the button's default focus outline */
      button.trigger:focus {
        outline: revert;
      }

      .trigger {
        display: block;
        cursor: pointer;
      }

      /* for manual activation, hide the content completely from screen readers when the tooltip is closed */
      /* otherwise, interactive elements may receive focus even when they are not visible */
      [data-manual] .content {
        visibility: hidden;
      }

      .content {
        box-sizing: border-box;
        font-family: var(--og-tooltip-family, inherit);
        font-size: var(--og-tooltip-size, inherit);
        color: var(--og-tooltip-color, inherit);
        background-color: var(--og-tooltip-background, #ececec);
        box-shadow: var(--og-tooltip-box-shadow, 2px 2px 6px rgba(0, 0, 0, 0.28));
        display: block;
        opacity: 0;
        padding: var(--og-tooltip-padding, 0.5em);
        text-align: var(--og-tooltip-text-align, left);
        pointer-events: none;
        position: absolute;
        transform: translateY(10px);
        transition: transform 0.25s ease-out;
        z-index: 99999;
        border-radius: var(--og-tooltip-border-radius, 0);
      }

      .content:after {
        content: ' ';
        height: 0;
        position: absolute;
        width: 0;
      }

      .top {
        bottom: 100%;
        margin-bottom: 10px;
      }

      .bottom {
        top: 100%;
        margin-top: 10px;
      }

      .left {
        right: 100%;
        margin-right: 10px;
      }

      .right {
        left: 100%;
        margin-left: 10px;
      }

      .top-left {
        bottom: 100%;
        margin-bottom: 10px;
        right: 100%;
        margin-right: -16px;
      }

      .top-right {
        bottom: 100%;
        margin-bottom: 10px;
        left: 100%;
        margin-left: -16px;
      }

      .bottom-left {
        top: 100%;
        margin-top: 10px;
        right: 100%;
        margin-right: -16px;
      }

      .bottom-right {
        top: 100%;
        margin-top: 10px;
        left: 100%;
        margin-left: -16px;
      }

      .bottom-left:after,
      .bottom-right:after,
      .top-left:after,
      .top-right:after,
      .top:after,
      .bottom:after {
        margin-left: -10px;
        left: 50%;
        border-left: solid transparent 10px;
        border-right: solid transparent 10px;
      }

      .top-left:after,
      .top-right:after,
      .top:after {
        bottom: -10px;
        border-top: solid var(--og-tooltip-background, #ececec) 10px;
      }
      .bottom-left:after,
      .top-left:after {
        left: auto;
        right: 0;
      }

      .bottom-right:after,
      .top-right:after {
        left: 0;
        right: auto;
        margin-left: 0;
      }

      .bottom-left:after,
      .bottom-right:after,
      .bottom:after {
        top: -10px;
        border-bottom: solid var(--og-tooltip-background, #ececec) 10px;
      }

      .left:after,
      .right:after {
        margin-top: -10px;
        top: 50%;
        border-top: solid transparent 10px;
        border-bottom: solid transparent 10px;
      }
      .right:after {
        left: -10px;
        border-right: solid var(--og-tooltip-background, #ececec) 10px;
      }
      .left:after {
        right: -10px;
        border-left: solid var(--og-tooltip-background, #ececec) 10px;
      }

      .tooltip[data-open] .content {
        visibility: visible;
        opacity: 1;
        width: 200px;
        pointer-events: auto;
        transform: translateY(0px);
      }
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.addEventListener('mouseenter', this.handleMouseEnter.bind(this), { signal });
    this.addEventListener('mouseleave', this.handleMouseLeave.bind(this), { signal });
    this.addEventListener('focusin', this.handleFocusIn.bind(this), { signal });
    this.addEventListener('focusout', this.handleFocusOut.bind(this), { signal });
    this.addEventListener('keydown', this.handleKeyDown.bind(this), { signal });

    document.addEventListener('click', this.handleDocumentClick.bind(this), { signal });
  }

  async recalculatePosition() {
    // wait for state changes to apply
    await this.updateComplete;
    if (!this.open) return;
    const trigger = this.shadowRoot.querySelector('.trigger');
    const triggerRect = trigger.getBoundingClientRect();
    const content = this.shadowRoot.querySelector('.content');
    const contentRect = content.getBoundingClientRect();
    if (!this.placement || this.placement === 'top' || this.placement === 'bottom')
      content.style.left = `${(-1 * contentRect.width + triggerRect.width) / 2}px`;
    else if (this.placement === 'left' || this.placement === 'right')
      content.style.top = `${(-1 * contentRect.height + triggerRect.height) / 2}px`;
  }

  handleMouseEnter() {
    this.open = true;
    this.recalculatePosition();
  }

  handleMouseLeave() {
    this.open = false;
  }

  handleFocusIn() {
    if (this.activationType !== ACTIVATION_TYPES.AUTOMATIC) return;
    this.open = true;
    this.recalculatePosition();
  }

  handleFocusOut(event) {
    if (this.activationType !== ACTIVATION_TYPES.AUTOMATIC) return;
    // keep the tooltip open if we're moving focus to another element inside the tooltip
    if (!this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  handleKeyDown(event) {
    // close the tooltip on Escape press, regardless of activation type.
    if (event.key === 'Escape' && this.open) {
      this.open = false;
      event.stopPropagation();
    }
  }

  handleClick() {
    if (this.activationType !== ACTIVATION_TYPES.MANUAL) return;
    this.open = !this.open;
    this.recalculatePosition();
  }

  handleDocumentClick(event) {
    if (this.activationType !== ACTIVATION_TYPES.MANUAL || !this.open) return;
    // close the tooltip if the user clicks outside of it
    if (!this.contains(event.target)) {
      this.open = false;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // remove event listeners
    this.abortController.abort();
  }

  render() {
    // allow removing aria-label by setting trigger-label to any falsy value
    // e.g. if the content inside the tooltip is sufficient
    const triggerLabel = this.triggerLabel ? this.triggerLabel : undefined;

    return html`
      <span class="tooltip" ?data-open="${this.open}" ?data-manual="${this.activationType === ACTIVATION_TYPES.MANUAL}">
        ${this.activationType === ACTIVATION_TYPES.MANUAL
          ? html`
              <button
                class="trigger"
                aria-label="${ifDefined(triggerLabel)}"
                aria-expanded="${this.open}"
                aria-controls="tooltip-content"
                @click="${this.handleClick}"
              >
                <slot name="trigger">${this.trigger}</slot>
              </button>
            `
          : html`
              <span
                class="trigger"
                tabindex="0"
                role="button"
                aria-label="${ifDefined(triggerLabel)}"
                aria-describedby="tooltip-content"
              >
                <slot name="trigger">${this.trigger}</slot>
              </span>
            `}
        <div class="content ${this.placement || 'bottom'}" role="tooltip" id="tooltip-content">
          <slot name="content">${this.content}</slot>
        </div>
      </span>
    `;
  }
}

export default Tooltip;
